import { useState } from 'react'
import { CalendarClock, Check, Loader2, MapPin, X } from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { acceptAssignment, declineAssignment, listMyAssignments } from './api'
import { assignmentRoleLabel, assignmentStatusLabel } from './vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageAssignmentResponse } from '@/shared/api/types'

/**
 * What this person has been asked to film.
 *
 * Accept and decline are gated on being the assignee, not on a permission — a
 * person answering their own assignment is not exercising a capability, and
 * requiring one would mean the field roles need a grant to say "I can't make it".
 */
export function CoverageAssignmentsPage() {
  const canRead = useCan('coverage:read')
  const [tick, setTick] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [declining, setDeclining] = useState<CoverageAssignmentResponse | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const { data, error: loadError, loading } = useLoad(() => listMyAssignments(), [tick])
  const rows = data ?? []

  if (!canRead) return <Forbidden what="live coverage" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="My Coverage"
        description="Events you have been asked to film, and the ones you have taken on."
      />
      <ErrorStrip message={error ?? loadError} />

      {loading && rows.length === 0 ? <LoadingBlock label="Loading your assignments…" />
        : rows.length === 0 ? (
          <EmptyState
            title="Nothing assigned to you"
            hint="When somebody books you to film an event, it appears here and you get an email."
          />
        ) : (
          <div className="space-y-3">
            {rows.map((a) => (
              <Panel
                key={a.id}
                title={a.eventTitle ?? 'Event'}
                description={`${assignmentRoleLabel(a.roleCode)} · ${assignmentStatusLabel(a.statusCode)}`}
                right={a.statusCode === 'ASSIGNED' ? (
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      className="erp-btn erp-btn-primary h-8"
                      disabled={busy === a.id}
                      onClick={async () => {
                        setBusy(a.id); setError(null)
                        try { await acceptAssignment(a.id); setTick((t) => t + 1) }
                        catch (e) { setError(explain(e)) } finally { setBusy(null) }
                      }}
                    >
                      {busy === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Accept
                    </button>
                    <button type="button" className="erp-btn h-8" onClick={() => setDeclining(a)}>
                      <X className="h-3.5 w-3.5" /> Decline
                    </button>
                  </div>
                ) : undefined}
              >
                <dl className="space-y-1 text-erp-sm">
                  {a.eventScheduledStart && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CalendarClock className="h-3.5 w-3.5" />
                      {new Date(a.eventScheduledStart).toLocaleString()}
                    </div>
                  )}
                  {a.eventRef && (
                    <div className="flex items-center gap-1.5 text-erp-xs text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" /> <span className="font-mono">{a.eventRef}</span>
                    </div>
                  )}
                  {a.note && <p className="text-erp-xs text-muted-foreground">{a.note}</p>}
                </dl>
                {a.statusCode === 'ACCEPTED' && (
                  <div className="mt-2">
                    <Note tone="good">
                      This camera is held for you. Being assigned does not by itself grant
                      permission to broadcast — if Go Live refuses, ask for the
                      broadcast permission.
                    </Note>
                  </div>
                )}
              </Panel>
            ))}
          </div>
        )}

      {declining && (
        <Modal open onClose={() => setDeclining(null)} title="Decline this assignment">
          <DeclineForm
            assignment={declining}
            onClose={() => setDeclining(null)}
            onDone={() => { setDeclining(null); setTick((t) => t + 1) }}
            onError={setError}
          />
        </Modal>
      )}
    </div>
  )
}

function DeclineForm({
  assignment, onClose, onDone, onError,
}: {
  assignment: CoverageAssignmentResponse
  onClose: () => void
  onDone: () => void
  onError: (m: string | null) => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <div className="space-y-3">
      <p className="text-erp-sm text-muted-foreground">
        Whoever assigned you is told, with your reason. A decline nobody hears is a no-show
        with paperwork.
      </p>
      <label className="block space-y-1">
        <span className="text-erp-xs font-medium">Why</span>
        <input className="erp-input w-full" value={reason} autoFocus
          onChange={(e) => setReason(e.target.value)}
          placeholder="Away that weekend / no transport / already on another event" />
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" className="erp-btn" onClick={onClose}>Keep it</button>
        <button
          type="button"
          className="erp-btn erp-btn-primary"
          disabled={!reason.trim() || busy}
          onClick={async () => {
            setBusy(true); onError(null)
            try { await declineAssignment(assignment.id, reason.trim()); onDone() }
            catch (e) { onError(explain(e)) } finally { setBusy(false) }
          }}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Decline
        </button>
      </div>
    </div>
  )
}
